import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Siren, ShieldCheck, Gauge, Users, MessagesSquare, Timer } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import KpiCard from '../components/ui/KpiCard';
import Badge from '../components/ui/Badge';
import { PageLoader } from '../components/ui/Spinner';
import DashboardMap from '../components/Dashboard/DashboardMap';
import DashboardService from '../services/DashboardService';
import AnnouncementService from '../services/AnnouncementService';

// Risk score, stakeholder-ack % and readiness/decon-throughput sliders have no backing
// model (no risk-model, stakeholder-ack, or decon-lane table exists yet). They're seeded
// from real casualty data where possible, then left as a manual what-if simulator —
// exactly what this panel is for — rather than invented as fake live telemetry.
function riskBaseline(casualties) {
  return Math.min(100, casualties.red * 15 + casualties.yellow * 8 + casualties.green * 3);
}

const STRATEGIES = [
  { key: 'openLane', title: 'Open standby decon lane', note: 'Reduces bottleneck and lowers operational risk.' },
  { key: 'authorityEscalation', title: 'Escalate stakeholder acknowledgement', note: 'Police and Lab/Public Health pending.' },
  { key: 'overflow', title: 'Activate overflow capacity', note: 'Prepare receiving ED/isolation and ambulance routes.' },
];

const INITIAL_SIM = { riskScore: null, stakeholderAck: 72, deconThroughput: 72 };

export default function ExecutiveDashboard() {
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [sim, setSim] = useState(INITIAL_SIM);
  const [selected, setSelected] = useState('openLane');
  const [loading, setLoading] = useState(true);

  const casualties = stats?.casualties ?? { total: 0, green: 0, yellow: 0, red: 0 };
  const totalMembers = stats?.totalMembers ?? 0;
  const activeMembers = (stats?.teamDataset ?? []).reduce((sum, t) => sum + (t.active || 0), 0);
  const readinessPct = totalMembers ? Math.round((activeMembers / totalMembers) * 100) : 0;

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await DashboardService.getStats();
        if (response?.status === 200) {
          setStats(response.data);
          setSim((s) => (s.riskScore === null ? { ...s, riskScore: riskBaseline(response.data.casualties) } : s));
        }
      } catch (error) {
        console.log(error);
      }
    };
    const loadAnnouncements = async () => {
      try {
        const response = await AnnouncementService.findAll(1, 100);
        if (response?.status === 200) {
          setAnnouncements(response.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    Promise.all([loadStats(), loadAnnouncements()]).finally(() => setLoading(false));
    const intervalId = setInterval(() => {
      loadStats();
      loadAnnouncements();
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const riskScore = sim.riskScore ?? 0;

  const applyAction = (key) => {
    setSelected(key);
    setSim((s) => {
      if (key === 'openLane') {
        return { ...s, riskScore: Math.max(0, s.riskScore - 8) };
      }
      if (key === 'authorityEscalation') {
        return { ...s, stakeholderAck: Math.min(100, s.stakeholderAck + 14), riskScore: Math.max(0, s.riskScore - 5) };
      }
      return { ...s, deconThroughput: Math.min(100, s.deconThroughput + 4), riskScore: Math.max(0, s.riskScore - 4) };
    });
    const messages = {
      openLane: 'Standby decon lane opened. KPI updated.',
      authorityEscalation: 'Stakeholder escalation sent. Acknowledgement KPI improved.',
      overflow: 'Overflow capacity prepared. Readiness improved.',
    };
    toast.success(messages[key]);
  };

  const simulateScenario = (type) => {
    if (type === 'surge') {
      setSim((s) => ({ ...s, riskScore: Math.min(100, s.riskScore + 12) }));
      toast.info('Patient surge simulated. Risk score updated.');
    }
    if (type === 'resourceBoost') {
      setSim((s) => ({
        ...s,
        deconThroughput: Math.min(100, s.deconThroughput + 12),
        riskScore: Math.max(0, s.riskScore - 10),
      }));
      toast.success('Resource boost applied. Risk score reduced.');
    }
    if (type === 'stakeholderDelay') {
      setSim((s) => ({
        ...s,
        stakeholderAck: Math.max(0, s.stakeholderAck - 12),
        riskScore: Math.min(100, s.riskScore + 7),
      }));
      toast.info('Stakeholder delay simulated. Risk score increased.');
    }
  };

  const riskBadgeVariant = riskScore >= 75 ? 'red' : riskScore >= 55 ? 'amber' : 'green';
  const riskLabel = riskScore >= 75 ? 'High Risk' : riskScore >= 55 ? 'Medium Risk' : 'Controlled Risk';

  const kpis = [
    { label: 'Executive Status', value: (stats?.activeIncidents ?? 0) > 0 ? 'Active' : 'Standby', note: `${stats?.activeIncidents ?? 0} CBRNE operation(s) in progress`, icon: Siren, type: 'red' },
    { label: 'Readiness', value: `${readinessPct}%`, note: `${activeMembers}/${totalMembers} members active`, icon: ShieldCheck, type: 'green' },
    { label: 'Risk Score', value: riskScore, note: 'Seeded from casualties, manual thereafter', icon: Gauge, type: 'amber' },
    { label: 'Patient Impact', value: casualties.total, note: `${casualties.red} red-severity`, icon: Users, type: 'blue' },
    { label: 'Stakeholder Ack', value: `${sim.stakeholderAck}%`, note: 'Manual estimate (not backend-tracked)', icon: MessagesSquare, type: 'amber' },
    { label: 'Next SITREP', value: '15m', note: 'Fixed executive briefing cadence', icon: Timer, type: 'blue' },
  ];

  if (loading) {
    return (
      <Sidebar title="High Level Dashboard">
        <PageLoader label="Loading executive dashboard…" />
      </Sidebar>
    );
  }

  return (
    <Sidebar title="High Level Dashboard">
      <section className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-6 gap-6">
          {kpis.map((k) => (
            <KpiCard key={k.label} {...k} />
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <DashboardMap announcements={announcements} height="420px" />
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 dark:bg-ink-900 dark:border-neutral-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-neutral-800 dark:text-neutral-100">Strategic Decision Support</h3>
              <Badge variant={riskBadgeVariant}>{riskLabel}</Badge>
            </div>
            <div className="mb-5">
              <div className="flex justify-between text-xs font-medium text-neutral-500 mb-2 dark:text-neutral-500">
                <span>Risk Score</span>
                <span>{riskScore} / 100</span>
              </div>
              <div className="risk-meter">
                <div className="risk-fill" style={{ width: `${Math.min(100, riskScore)}%` }} />
              </div>
            </div>
            <div className="space-y-3">
              {STRATEGIES.map((s) => (
                <div key={s.key} className={`strategy-card ${selected === s.key ? 'selected' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-bold text-neutral-900 text-sm dark:text-neutral-50">{s.title}</h4>
                      <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">{s.note}</p>
                    </div>
                    <button className="badge badge-blue" onClick={() => applyAction(s.key)}>
                      Apply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 dark:bg-ink-900 dark:border-neutral-700">
            <h3 className="font-bold text-neutral-800 mb-4 dark:text-neutral-100">Disaster Scale Scenario Simulator</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="scenario-control flex justify-between items-center border border-neutral-200 rounded-xl p-3 bg-white dark:border-neutral-700 dark:bg-ink-900">
                <div>
                  <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">Patient Surge</p>
                  <small className="text-neutral-500 dark:text-neutral-500">Simulate +10 patients</small>
                </div>
                <button className="badge badge-blue" onClick={() => simulateScenario('surge')}>Apply</button>
              </div>
              <div className="scenario-control flex justify-between items-center border border-neutral-200 rounded-xl p-3 bg-white dark:border-neutral-700 dark:bg-ink-900">
                <div>
                  <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">Resource Boost</p>
                  <small className="text-neutral-500 dark:text-neutral-500">Add PPE / lane / ambulance</small>
                </div>
                <button className="badge badge-green" onClick={() => simulateScenario('resourceBoost')}>Apply</button>
              </div>
              <div className="scenario-control flex justify-between items-center border border-neutral-200 rounded-xl p-3 bg-white dark:border-neutral-700 dark:bg-ink-900">
                <div>
                  <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">Stakeholder Delay</p>
                  <small className="text-neutral-500 dark:text-neutral-500">Reduce acknowledgement</small>
                </div>
                <button className="badge badge-amber" onClick={() => simulateScenario('stakeholderDelay')}>Apply</button>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-6 dark:bg-ink-900 dark:border-neutral-700">
            <h3 className="font-bold text-neutral-800 mb-4 dark:text-neutral-100">Executive Actions</h3>
            <button className="action-button" onClick={() => toast.success('Executive brief exported.')}>
              Open Executive Brief
            </button>
            <button className="action-button" onClick={() => applyAction('authorityEscalation')}>
              Escalate to Authority
            </button>
            <button className="action-button" onClick={() => toast.success('Command conference started.')}>
              Join Command Conference
            </button>
          </div>
        </div>
      </section>
    </Sidebar>
  );
}
